import { GraphQLError } from "graphql";
import { format, isValid, parseISO } from "date-fns";
import {
  cartAndItemErrorMap,
  genericErrorMsgEnglish,
  longLocalizedDateFormatDateFns,
} from "../constants";
import {
  logWithMessageInSentry,
  logUnknownErrorCode,
} from "./logAndCaptureInSentry";
import {
  GenericErrorExtensions,
  LoginErrorKeys,
  SignupErrorKeys,
  CartErrorMsg,
  ItemErrorDetail,
  ItemErrorMsg,
  ItemWithErrorMsgs,
  SubmitOrderErrorKeys,
  VariantErrorDetail,
} from "../types/error";
import {
  CheckoutErrorsLocalizations,
  FormsSignInLocalization,
} from "../types/graphql/generated";
import { parseTemplateAsString } from "../helpers/localization";
import { convertFromCentsToDollars } from "../helpers/currency";

export const parseLoginUserGraphQLErrors = (
  graphQLErrors: GraphQLError[],
  localization?: FormsSignInLocalization | null
) => {
  let errorMsg = "";
  let passwordResetRequired = false;
  let emailVerificationRequired = false;

  // 4 possible error keys are: error, account_locked, password_reset_required, emailVerificationRequired

  graphQLErrors.forEach((error: GraphQLError) => {
    const errorDetails =
      (error.extensions as GenericErrorExtensions<LoginErrorKeys>)?.details ||
      [];
    // non-array errorDetails will get logged to Sentry in errorLink
    if (!Array.isArray(errorDetails)) return;
    errorDetails.forEach((errorDetail) => {
      const { key } = errorDetail;
      switch (key) {
        case LoginErrorKeys.accountLocked:
          errorMsg =
            localization?.accountLockedErrorText ||
            "Account locked. Please contact support for assistance.";
          break;
        case LoginErrorKeys.authError:
        case LoginErrorKeys.error:
          errorMsg =
            localization?.genericErrorText ||
            "Oops, that email and password combination isn't correct. Please check and try again or try resetting your password.";
          break;
        case LoginErrorKeys.passwordResetRequired:
          errorMsg = "";
          passwordResetRequired = true;
          break;
        case LoginErrorKeys.emailVerificationRequired:
          errorMsg = "";
          emailVerificationRequired = true;
          break;
        default:
          logUnknownErrorCode(key, "loginUser", errorDetail);
          errorMsg = genericErrorMsgEnglish;
      }
    });
  });

  return { errorMsg, passwordResetRequired, emailVerificationRequired };
};

export const parseSignupUserGraphQLErrors = (graphQLErrors: GraphQLError[]) => {
  let errorMsg = "";
  let emailVerificationRequired = false;

  graphQLErrors.forEach((error: GraphQLError) => {
    const errorDetails =
      (error.extensions as GenericErrorExtensions<SignupErrorKeys>)?.details ||
      [];
    // non-array errorDetails will get logged to Sentry in errorLink
    if (!Array.isArray(errorDetails)) return;
    errorDetails.forEach((errorDetail) => {
      const { key } = errorDetail;
      switch (key) {
        case SignupErrorKeys.authError:
        case SignupErrorKeys.unverifiedEmail:
          errorMsg = genericErrorMsgEnglish;
          break;
        case SignupErrorKeys.emailVerificationRequired:
          emailVerificationRequired = true;
          break;
        default:
          logUnknownErrorCode(key, "signupUser", errorDetail);
          errorMsg = genericErrorMsgEnglish;
      }
    });
  });

  return { errorMsg, emailVerificationRequired };
};

type LocalizationCheckoutErrors = CheckoutErrorsLocalizations | null;

/**
 * This is needed because the type returned from validation errors is almost
 * the same as the key from the GQL error, but not quite, but the error
 * handling is the same, so we parse it to the existing SubmitOrderErrorKey.
 * Eventually we want BE to fix this so the keys are the same, but it is WIP.
 *
 * @param typename ValidationError
 * @returns SubmitOrderErrorKey
 *
 * ex: ArchivedItemsInCartValidationError => archivedItemsInCart
 */
export const mapTypenameToErrorKey = (typename = ""): SubmitOrderErrorKeys => {
  const key = typename
    .replace(/ValidationError$/, "")
    .replace(/^./, (c) => c.toLowerCase());

  // annoyingly this one doesn't currently map correctly
  if (key === "shippingAddress")
    return SubmitOrderErrorKeys.shippingAddressValidation;

  if (!(key in SubmitOrderErrorKeys)) {
    const msg =
      `Unknown validation error "${typename}": no matching ` +
      `SubmitOrderErrorKey of "${key}", must be mapped or error will not ` +
      `show to user`;
    logWithMessageInSentry(msg);
  }

  return key as SubmitOrderErrorKeys;
};

export const getCartErrorMsg = (
  errorKey: SubmitOrderErrorKeys,
  options?: {
    amount?: number;
    earliestScheduledOrderDeliveryDate?: string | null;
    quantity?: number;
  },
  localization?: LocalizationCheckoutErrors
): CartErrorMsg | null => {
  const { amount, earliestScheduledOrderDeliveryDate, quantity } =
    options || {};
  switch (errorKey) {
    case "guestCartContainsSubscription":
      return {
        alertType: "error",
        msg:
          localization?.guestCartContainsSubscription?.cartText ||
          cartAndItemErrorMap.guestCartContainsSubscription.cart,
        errorKey,
      };
    case "minimumPurchaseAmount":
      return {
        alertType: "error",
        msg: parseTemplateAsString(
          localization?.minimumPurchaseAmount ||
            cartAndItemErrorMap.minimumPurchaseAmount.cart,
          { minValue: convertFromCentsToDollars(amount || 0) }
        ),
        errorKey,
      };
    case "minimumPurchaseQuantity":
      return {
        alertType: "error",
        msg: parseTemplateAsString(
          localization?.minimumPurchaseQuantity ||
            cartAndItemErrorMap.minimumPurchaseQuantity.cart,
          { minValue: quantity || "" }
        ),
        errorKey,
      };
    case "lowStock":
    case "variantLowStock":
      return {
        alertType: "warning",
        msg:
          localization?.lowStock?.cartText || cartAndItemErrorMap.lowStock.cart,
        errorKey,
      };
    case "outOfStock":
    case "variantOutOfStock": {
      const scheduledOrderDeliveryDate = earliestScheduledOrderDeliveryDate
        ? parseISO(earliestScheduledOrderDeliveryDate)
        : null;

      // `format` throws on an unparseable date, which would take down the whole
      // checkout render, so fall back to the undated copy below instead.
      if (scheduledOrderDeliveryDate && isValid(scheduledOrderDeliveryDate)) {
        return {
          alertType: "warning",
          msg: parseTemplateAsString(
            localization?.outOfStock?.scheduledOrderCartText ||
              cartAndItemErrorMap.outOfStock.scheduledOrderCart ||
              cartAndItemErrorMap.outOfStock.cart,
            {
              scheduledOrderDeliveryDate: format(
                scheduledOrderDeliveryDate,
                longLocalizedDateFormatDateFns
              ),
            }
          ),
          errorKey,
        };
      }

      return {
        alertType: "error",
        msg:
          localization?.outOfStock?.cartText ||
          cartAndItemErrorMap.outOfStock.cart,
        errorKey,
      };
    }
    case "purchaseQtyLimit":
      return {
        alertType: "warning",
        msg:
          localization?.purchaseQtyLimit?.cartText ||
          cartAndItemErrorMap.purchaseQtyLimit.cart,
        errorKey,
      };
    case "scheduledOrderOutOfStock":
      return {
        alertType: "warning",
        msg:
          localization?.scheduledOrderOutOfStock?.cartText ||
          cartAndItemErrorMap.scheduledOrderOutOfStock.cart,
        errorKey,
      };
    case "variantBelowMinQuantity":
      return {
        alertType: "error",
        msg: cartAndItemErrorMap.variantBelowMinQuantity.cart,
        errorKey,
      };
    case "variantExceedsMaximumQuantity":
      return {
        alertType: "error",
        msg: cartAndItemErrorMap.variantExceedsMaximumQuantity.cart,
        errorKey,
      };
    case "shippingAddressValidation":
      return {
        alertType: "error",
        msg: cartAndItemErrorMap.shippingAddressValidation.cart,
        errorKey,
      };
    case "unverifiedEmail":
      return {
        alertType: "error",
        msg: cartAndItemErrorMap.unverifiedEmail.cart,
        errorKey,
      };
    case "subscriptionsNotAllowed":
    case "variantSubscriptionsNotAllowed":
      return {
        alertType: "error",
        msg: cartAndItemErrorMap.subscriptionsNotAllowed.cart,
        errorKey,
      };
    case "archivedItemsInCart":
    case "assortmentExclusion":
    case "assortmentInclusion":
    case "bundleItemWithoutBundle":
    case "bundleOnlyWithoutBundle":
    case "subscriptionOnlyInCart":
    case "unapprovedItemsInCart":
    case "variantArchived":
    case "variantInvalid":
    case "variantInvalidSubscription":
    case "virtualItemsInCart":
      return {
        alertType: "error",
        msg:
          localization?.itemUnavailable?.cartText ||
          cartAndItemErrorMap.itemUnavailable.cart,
        errorKey,
      };
    default:
      return null;
  }
};

export const getItemErrorMsg = (
  errorKey: SubmitOrderErrorKeys,
  options: {
    inventory?: number;
    isScheduledOrderEligible?: boolean;
    maxQuantity?: number;
    minQuantity?: number;
    quantity?: number;
    purchaseQtyLimit?: number;
  },
  localization?: LocalizationCheckoutErrors
): ItemErrorMsg | null => {
  const {
    inventory,
    isScheduledOrderEligible,
    maxQuantity,
    minQuantity,
    purchaseQtyLimit,
  } = options;
  switch (errorKey) {
    case "guestCartContainsSubscription":
      return {
        alertType: "error",
        title:
          localization?.guestCartContainsSubscription?.itemHeadingText ||
          cartAndItemErrorMap.guestCartContainsSubscription.title,
        note:
          localization?.guestCartContainsSubscription?.itemNoteText ||
          cartAndItemErrorMap.guestCartContainsSubscription.note,
      };
    case "lowStock":
    case "variantLowStock":
      return {
        alertType: "warning",
        title: parseTemplateAsString(
          localization?.lowStock?.itemHeadingText ||
            cartAndItemErrorMap.lowStock.title,
          { inventoryQty: inventory || "" }
        ),
        note:
          localization?.lowStock?.itemNoteText ||
          cartAndItemErrorMap.lowStock.note,
      };
    case "purchaseQtyLimit":
      return {
        alertType: "warning",
        title: parseTemplateAsString(
          cartAndItemErrorMap.purchaseQtyLimit.title,
          { purchaseQtyLimit: purchaseQtyLimit || "" }
        ),
        note: cartAndItemErrorMap.purchaseQtyLimit.note,
      };
    case "outOfStock":
    case "variantOutOfStock":
      return {
        alertType: isScheduledOrderEligible ? "warning" : "error",
        title:
          localization?.outOfStock?.itemHeadingText ||
          cartAndItemErrorMap.outOfStock.title,
        note: isScheduledOrderEligible
          ? ""
          : localization?.outOfStock?.itemNoteText ||
            cartAndItemErrorMap.outOfStock.note,
      };
    case "zipCodeRestrictionOnItems":
    case "zipCodeRestrictionOnTenant":
    case "variantZipCodeRestriction":
      return {
        alertType: "error",
        title:
          localization?.zipCodeRestriction?.itemHeadingText ||
          cartAndItemErrorMap.zipCodeRestrictionOnItems.title,
        note:
          localization?.zipCodeRestriction?.itemNoteText ||
          cartAndItemErrorMap.zipCodeRestrictionOnItems.note,
      };
    case "variantBelowMinQuantity":
      return {
        alertType: "warning",
        title: parseTemplateAsString(
          cartAndItemErrorMap.variantBelowMinQuantity.title,
          {
            minQty: minQuantity || "",
          }
        ),
        note: cartAndItemErrorMap.variantBelowMinQuantity.note,
      };
    case "variantExceedsMaximumQuantity":
      return {
        alertType: "warning",
        title: parseTemplateAsString(
          cartAndItemErrorMap.variantExceedsMaximumQuantity.title,
          { maxQty: maxQuantity || "" }
        ),
        note: cartAndItemErrorMap.variantExceedsMaximumQuantity.note,
      };
    case "subscriptionsNotAllowed":
    case "variantSubscriptionsNotAllowed":
      return {
        alertType: "error",
        title: cartAndItemErrorMap.subscriptionsNotAllowed.title,
        note: cartAndItemErrorMap.subscriptionsNotAllowed.note,
      };
    case "archivedItemsInCart":
    case "assortmentExclusion":
    case "assortmentInclusion":
    case "bundleItemWithoutBundle":
    case "bundleOnlyWithoutBundle":
    case "subscriptionOnlyInCart":
    case "unapprovedItemsInCart":
    case "variantArchived":
    case "variantInvalid":
    case "variantInvalidSubscription":
    case "virtualItemsInCart":
      return {
        alertType: "error",
        title:
          localization?.itemUnavailable?.itemHeadingText ||
          cartAndItemErrorMap.itemUnavailable.title,
        note:
          localization?.itemUnavailable?.itemNoteText ||
          cartAndItemErrorMap.itemUnavailable.note,
      };
    default:
      return null;
  }
};

export const handleAddCartError = (
  cartErrors: CartErrorMsg[],
  errorKey: SubmitOrderErrorKeys,
  localization?: LocalizationCheckoutErrors,
  options?: {
    amount?: number;
    earliestScheduledOrderDeliveryDate?: string | null;
    quantity?: number;
  }
): CartErrorMsg[] => {
  const cartErrorMsg = getCartErrorMsg(errorKey, options, localization);
  // dedupe messages
  if (cartErrorMsg && !cartErrors.some((err) => err.msg === cartErrorMsg.msg)) {
    cartErrors.push(cartErrorMsg);
  }
  return cartErrors;
};

export const handleAddItemError = (
  itemErrors: ItemWithErrorMsgs[],
  type: SubmitOrderErrorKeys,
  details: ItemErrorDetail[],
  localization?: LocalizationCheckoutErrors,
  options?: { isScheduledOrderEligible?: boolean }
): ItemWithErrorMsgs[] => {
  details.map((detail) => {
    const { id, inventoryQty, purchaseQtyLimit } = detail;
    const msg = getItemErrorMsg(
      type,
      {
        isScheduledOrderEligible: options?.isScheduledOrderEligible,
        quantity: inventoryQty,
        purchaseQtyLimit,
      },
      localization
    );
    if (!msg) return;

    // if item aready in errors list, add to that item's list of errors
    const item = itemErrors.find((item) => item.id === id);
    if (item) {
      item.errorMsgs.push(msg);
    } else {
      itemErrors.push({
        id,
        errorMsgs: [msg],
      });
    }
  });
  return itemErrors;
};

export const handleAddVariantError = (
  itemErrors: ItemWithErrorMsgs[],
  type: SubmitOrderErrorKeys,
  detail: VariantErrorDetail,
  localization?: LocalizationCheckoutErrors,
  options?: { isScheduledOrderEligible?: boolean }
): ItemWithErrorMsgs[] => {
  const { cartItemId, inventory, maxQuantity, minQuantity, quantity } = detail;
  const msg = getItemErrorMsg(
    type,
    {
      inventory,
      isScheduledOrderEligible: options?.isScheduledOrderEligible,
      maxQuantity,
      minQuantity,
      quantity,
    },
    localization
  );
  if (!msg) return itemErrors;

  // if item aready in errors list, add to that item's list of errors
  const item = itemErrors.find((item) => item.id === cartItemId);
  if (item) {
    item.errorMsgs.push(msg);
  } else {
    itemErrors.push({
      id: cartItemId,
      errorMsgs: [msg],
    });
  }
  return itemErrors;
};
